import React from 'react';
import { useEffect, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import {
    Box,
    Button,
    ButtonGroup,
    Checkbox,
    CircularProgress,
    FormControlLabel,
    IconButton,
    InputAdornment,
    TextField,
    Tooltip,
    FormLabel,
    RadioGroup,
    Radio
} from "@mui/material";
import InfoIcon from '@mui/icons-material/Info';
import { styled } from "@mui/material/styles";
import { blueGrey } from "@mui/material/colors";
import RemoveIcon from "@mui/icons-material/Remove";
import { RootState, useAppDispatch, useAppSelector } from "../../../state/state.tsx";
import { closeModal, toggleLoading } from "../../../state/ModalSlice/ModalSlice.ts";
import { useBackend } from "../../../hooks/backend/useBackend.ts";
import { getFutureCycles } from "../utilityFunctions.ts";
import { doc, getDocs, Timestamp, getDoc } from "firebase/firestore";
import { db, SECRETS_ENV } from "../../../Config/firebase.ts";
import { getQuery } from "../../../hooks/backend/getQuery.ts";
import { toast } from "react-toastify";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from '@mui/icons-material/Add';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { SubscriptionType } from '../../../enums.ts';
import { selectEffectiveUID } from '../../../state/UserStatesV2/userSliceV2.ts';
import { CustomerKYCFieldType } from './CustomerKYCModal';


const secretCloudTaskToken = import.meta.env.VITE_SECRET_CLOUD_TASK_TOKEN || process.env.VITE_SECRET_CLOUD_TASK_TOKEN

// -------------------- Counter Config
const StyledButton = styled(Button)(({ theme }) => ({
    color: theme.palette.getContrastText(blueGrey[50]),
    backgroundColor: blueGrey[50],
    borderColor: blueGrey[200],
    height: '35px', // Set height to 30px
    "&:hover": {
        backgroundColor: blueGrey[100],
        borderColor: blueGrey[300],
    },
}));


const StyledInput = styled(TextField)({
    "& .MuiOutlinedInput-root": {
        "& fieldset": {
            borderRadius: 0,
            borderColor: blueGrey[200],
            height: '40px', // Set height to 30px
        },
        "&:hover fieldset": {
            borderColor: blueGrey[300],
        },
        "&.Mui-focused fieldset": {
            borderColor: blueGrey[500],
        },
        "& input": {
            textAlign: "center",
            width: 60,
            color: blueGrey[700],
            height: '100%', // Ensure input takes full height of the container
            padding: '10 0 0 0', // Remove padding to maintain the 30px height
        },
    },
});


export default function AddLiveSubscriptionModal() {

    // ------------------------DialogHandlers

    const Dispatch = useAppDispatch();
    const open = useAppSelector((state: RootState) => state.ModalSlice.AddLiveSubscriptionModalOpen);
    const isLoading = useAppSelector((state: RootState) => state.ModalSlice.isLoading)
    const handleClose = () => {
        Dispatch(closeModal("AddLiveSubscriptionModalOpen"))
        resetState()
    };

    const ServiceProviderId: string = useAppSelector(selectEffectiveUID) 

    // ------------------------Form States

    const [profilePhoto, setProfilePhoto] = useState<string>('https://firebasestorage.googleapis.com/v0/b/coachprodev.appspot.com/o/Config%2FProfilePhoto2Update.png?alt=media&token=53ea668c-04f3-493a-911d-15eba1ce897c');
    const [subscriberName, setSubscriberName] = useState<string>("")
    const [payeeName, setPayeeName] = React.useState<string>("")
    const [price, setPrice] = React.useState<string>("")
    const [discount, setDiscount] = React.useState<string>("0")
    const [payeeNumber, setPayeeNumber] = React.useState<string>("")
    const [multiplier, setMultiplier] = useState<number>(1);
    const [, setIsError] = React.useState<boolean>(false)
    const [payNow, setPayNow] = React.useState<boolean>(true)
    const [showAdvanced, setShowAdvanced] = React.useState<boolean>(false)
    const [cycleName, setCycleName] = useState<string>(getFutureCycles()[0])
    const [showMultiplier, setShowMultiplier] = React.useState<boolean>(false)
    const [iPay, setIPay] = useState<boolean>(false)
    const [advance, setAdvance] = useState<string>("")
    const [showPayeeName, setShowPayeeName] = useState<boolean>(false)
    const [CollectivePackPrice, setCollectivePackPrice] = useState<string>("")
    const [packField, setPackField] = useState<string>("")
    const [monthlyPrice, setMonthlyPrice] = useState<string>("")
    const [products, setProducts] = useState([{ name: '', price: '0' }]);
    const [productErrors, setProductErrors] = useState<{ name: boolean; price: boolean }[]>([{ name: false, price: false }]);
    const [startDate, setStartDate] = useState<Date>(new Date());
    const [endDate, setEndDate] = useState<Date>(new Date());
    const [selectedCyclePlan, setSelectedCyclePlan] = useState<any>(null);
    const [cyclePriceArray, setCyclePriceArray] = useState<any[]>([]);
    const [PaymentCycle, setPaymentCycle] = useState<string>("")
    const [payeeEmail, setPayeeEmail] = useState<string>("");
    const [secondaryMobile, setSecondaryMobile] = useState<string>("");
    const [emailError, setEmailError] = useState<string>("");
    const [secondaryPayeeName, setSecondaryPayeeName] = useState<string>("");
    const [secondaryEmail, setSecondaryEmail] = useState<string>("");
    const [customPrice, setCustomPrice] = useState<string>("")
    const [selectedCyclePlanIndex, setSelectedCyclePlanIndex] = useState<number>(0);
    const [outsideState, setOutsideState] = useState<boolean>(false);
    const [consumerGst, setConsumerGst] = useState<string>("");
    const [consumerGstError, setConsumerGstError] = useState<boolean>(false);
    
    // CustomerKYC related states
    const [customerKYCFields, setCustomerKYCFields] = useState<CustomerKYCFieldType[]>([]);
    const [kycResponses, setKycResponses] = useState<{[key: number]: any}>({});

    // -----------------------Fetch states from Store

    const quickAddFields = useAppSelector((state: RootState) => state.ArbitrarySlice.ForQuickAddModal)
    const subscriptionId = quickAddFields.id
    useEffect(() => {
        if (quickAddFields) {

            setMonthlyPrice(quickAddFields.Price?.toString() || "0")
            setAdvance(quickAddFields.advance?.toString() || "0")
            setShowMultiplier(quickAddFields.type === "Preschool" || quickAddFields.type === "Sports" || quickAddFields.type === "Creche" || quickAddFields.type === "Tution" || quickAddFields.type === "Tuition" || quickAddFields.type === "Fitness" || quickAddFields.type === "Other")
            if (quickAddFields.type === "Preschool" || quickAddFields.type === "Sports" || quickAddFields.type === "Creche" || quickAddFields.type === "Tution" || quickAddFields.type === "Tuition" || quickAddFields.type === "Fitness" || quickAddFields.type === "Other") {
                setShowPayeeName(true)
            } else {
                setShowPayeeName(false)
            }
        }

        setCycleName(getFutureCycles()[0])

        // Set start date to current date when modal opens
        if (open) {
            setStartDate(new Date())
        }

    }, [quickAddFields, open])

    // Add useEffect to fetch subscription details
    useEffect(() => {
        const fetchSubscriptionData = async () => {
            if (subscriptionId && open) {
                try {
                    const subscriptionDocPath = `/ServiceProviders/${ServiceProviderId}/Subscriptions/${subscriptionId}`;
                    const subscriptionDocRef = doc(db, subscriptionDocPath);
                    const subscriptionDoc = await getDoc(subscriptionDocRef);

                    if (subscriptionDoc.exists()) {
                        const data = subscriptionDoc.data();
                        // Set form states based on subscription data
                        setAdvance(data.advance?.toString() || "0");


                        setMonthlyPrice(data.Price?.toString() || "0");

                        // Determine if multiplier should be shown based on subscription type
                        const showMultiplierTypes = [
                            "Preschool", "Sports", "Creche", "Tution",
                            "Tuition", "Fitness", "Other"
                        ];
                        const shouldShowMultiplier = showMultiplierTypes.includes(data.type);
                        setShowMultiplier(shouldShowMultiplier);
                        setShowPayeeName(shouldShowMultiplier);
                        setPaymentCycle(data.subscriptionType || "");

                        if (data.subscriptionType === "MultiMonth") {
                            setCyclePriceArray(data.cyclePriceArray || []);
                            // Set the first plan as default
                            if (data.cyclePriceArray && data.cyclePriceArray.length > 0) {
                                setSelectedCyclePlan(data.cyclePriceArray[0]);
                                setMonthlyPrice(data.cyclePriceArray[0].price.toString());
                            }
                        } else if (data.subscriptionType === "Count") {
                            setCyclePriceArray(data.cyclePriceArray || []);
                            // Set the first plan as default
                            if (data.cyclePriceArray && data.cyclePriceArray.length > 0) {
                                setSelectedCyclePlan(data.cyclePriceArray[0]);
                                setMonthlyPrice(data.cyclePriceArray[0].price.toString());
                            }
                        }
                    }
                } catch (error) {
                    console.error("Error fetching subscription data:", error);
                    toast.error("Error fetching subscription details");
                }
            }
        };

        fetchSubscriptionData();
    }, [subscriptionId, open, ServiceProviderId]);

    // Fetch CustomerKYC fields when modal opens
    useEffect(() => {
        const fetchCustomerKYCFields = async () => {
            if (ServiceProviderId && open) {
                try {
                    const serviceProviderRef = doc(db, 'ServiceProviders', ServiceProviderId);
                    const serviceProviderDoc = await getDoc(serviceProviderRef);
                    
                    if (serviceProviderDoc.exists()) {
                        const data = serviceProviderDoc.data();
                        const kycFields = data.customerKYCFields || [];
                        setCustomerKYCFields(kycFields);
                        
                        // Initialize KYC responses
                        const initialResponses: {[key: number]: any} = {};
                        kycFields.forEach((field: CustomerKYCFieldType) => {
                            if (field.type === 'checkbox') {
                                initialResponses[field.id] = false;
                            } else if (field.type === 'dropdown' && field.options && field.options.length > 0) {
                                initialResponses[field.id] = field.options[0];
                            } else {
                                initialResponses[field.id] = '';
                            }
                        });
                        setKycResponses(initialResponses);
                    }
                } catch (error) {
                    console.error("Error fetching CustomerKYC fields:", error);
                }
            }
        };

        fetchCustomerKYCFields();
    }, [ServiceProviderId, open]);

    // ------------------ Update pay Now Dynamically

    useEffect(() => {
        if (quickAddFields) {
            const productsTotal = products.reduce((sum, product) => sum + Number(product.price), 0);

            if (quickAddFields.type !== "CableTV") {

                // For monthly subscriptions, first payment includes monthly fee + products + advance
                setPrice((Number(monthlyPrice) + productsTotal + Number(advance)).toString());

            } else {
                setPrice((productsTotal).toString());
            }
        }
    }, [advance, quickAddFields, CollectivePackPrice, products, monthlyPrice, PaymentCycle]);



    // ----------------------- Reset States

    const resetState = () => {
        setProfilePhoto("https://firebasestorage.googleapis.com/v0/b/coachprodev.appspot.com/o/Config%2FProfilePhoto2Update.png?alt=media&token=53ea668c-04f3-493a-911d-15eba1ce897c")
        setSubscriberName("")
        setPayeeName("")
        setPayeeNumber("")
        setPrice("")
        setDiscount("")
        setMultiplier(1)
        setModalError({
            subscriberName: false, payeeName: false, price: false, payeeNumber: false, CollectivePackPrice: false, monthlyPrice: false, secondaryMobile: false, email: false, secondaryPayeeName: false, secondaryEmail: false, customPrice: false, consumerGst: false
        })
        setIPay(false)
        setShowAdvanced(false)
        setPackField("")
        setCollectivePackPrice("")
        setProducts([{ name: '', price: '0' }]);
        setProductErrors([{ name: false, price: false }]);
        setPayNow(true)
        // Reset subscription plan to first option if available
        if (cyclePriceArray.length > 0) {
            setSelectedCyclePlan(cyclePriceArray[0]);
            // Handle different price field names based on payment cycle
            if (PaymentCycle === "Count") {
                setMonthlyPrice(cyclePriceArray[0].price?.toString() || "0");
            } else {
                setMonthlyPrice(cyclePriceArray[0].price?.toString() || "0");
            }
        }
        setSecondaryMobile("")
        setPayeeEmail("")
        setSecondaryPayeeName("")
        setSecondaryEmail("")
        setStartDate(new Date())
        setSelectedCyclePlanIndex(0)
        setOutsideState(false)
        setConsumerGst("")
        setConsumerGstError(false)
        
        // Reset KYC responses
        const initialResponses: {[key: number]: any} = {};
        customerKYCFields.forEach((field: CustomerKYCFieldType) => {
            if (field.type === 'checkbox') {
                initialResponses[field.id] = false;
            } else if (field.type === 'dropdown' && field.options && field.options.length > 0) {
                initialResponses[field.id] = field.options[0];
            } else {
                initialResponses[field.id] = '';
            }
        });
        setKycResponses(initialResponses);
        
    };

    // ------------------------SetOptions


    useEffect(() => {
        if (PaymentCycle === "MultiMonth" || PaymentCycle === "Count") {
            setCustomPrice(monthlyPrice)
        }
    }, [monthlyPrice])

    // ------------------------Handle Change Form States

    const handleChangePayeeName = (event: React.ChangeEvent<HTMLInputElement>) => {
        setPayeeName(event.target.value)
    }

    const handleChangeSubscriberName = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSubscriberName(event.target.value)
    }
    const { uploadFileInFirestore } = useBackend("")

    async function handlePhotoChange(e: any) {
        const file = e.target.files[0];
        const url: string = await uploadFileInFirestore(ServiceProviderId, file)
        setProfilePhoto(url)
    }

    const handleChangePrice = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = event.target;
        // -------------Function to handle only decimal and number inputs
        if (!value) {
            setPrice("")
            return;
        }
        let decimalCount = 0;
        for (let i = 0; i < value.length; i++) {
            const char = value[i];
            if (char >= '0' && char <= '9') { /* empty */
            } else if (char === '.') {
                if (decimalCount === 0) {
                    decimalCount++;
                } else {
                    return;
                }
            } else {
                return;
            }
        }
        setPrice(value)
    }
    const handleChangeDiscount = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = event.target;
        // Function to handle only decimal and number inputs
        if (!value) {
            setDiscount("")
            return;
        }
        let decimalCount = 0;
        for (let i = 0; i < value.length; i++) {
            const char = value[i];
            if (char >= '0' && char <= '9') { /* empty */
            } else if (char === '.') {
                if (decimalCount === 0) {
                    decimalCount++;
                } else {
                    return;
                }
            } else {
                return;
            }
        }
        setDiscount(value)
    }
    const handleChangeAdvance = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = event.target;
        // -------------Function to handle only decimal and number inputs
        if (!value) {
            setAdvance("")
            return;
        }
        let decimalCount = 0;
        for (let i = 0; i < value.length; i++) {
            const char = value[i];
            if (char >= '0' && char <= '9') { /* empty */
            } else if (char === '.') {
                if (decimalCount === 0) {
                    decimalCount++;
                } else {
                    return;
                }
            } else {
                return;
            }
        }
        setAdvance(value)
    }


    const handleChangeIPay = (event: React.ChangeEvent<HTMLInputElement>) => {
        setIPay(event.target.checked)
        if (event.target.checked) {
            setPayeeName(subscriberName)
        }
    }
    const handleChangePayeeNumber = (value: string) => {
        setPayeeNumber(value);
    }
    const handleChangeMultiplier = (event: { target: { value: any; }; }) => {
        setMultiplier(Math.max(Number(event.target.value), 1));
    };
    const handleChangePayNow = (event: React.ChangeEvent<HTMLInputElement>) => {
        setPayNow(event.target.checked)
    }
    const handleChangeShowAdvanced = (event: React.ChangeEvent<HTMLInputElement>) => {
        setShowAdvanced(event.target.checked)
    }

    const handleChangeMonthlyPrice = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = event.target;
        if (!value) {
            setMonthlyPrice("")
            return;
        }
        let decimalCount = 0;
        for (let i = 0; i < value.length; i++) {
            const char = value[i];
            if (char >= '0' && char <= '9') { /* empty */
            } else if (char === '.') {
                if (decimalCount === 0) {
                    decimalCount++;
                } else {
                    return;
                }
            } else {
                return;
            }
        }
        setMonthlyPrice(value)
    }

    const handleChangeCustomPrice = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = event.target;
        if (!value) {
            setCustomPrice("")
            return;
        }
        let decimalCount = 0;
        for (let i = 0; i < value.length; i++) {
            const char = value[i];
            if (char >= '0' && char <= '9') { /* empty */
            } else if (char === '.') {
                if (decimalCount === 0) {
                    decimalCount++;
                } else {
                    return;
                }
            } else {
                return;
            }
        }

        setCustomPrice(event.target.value)
    }

    // const handleAddProduct = () => {
    //     setProducts([...products, { name: '', price: '0' }]);
    //     setProductErrors([...productErrors, { name: false, price: false }]);
    // };

    // const handleRemoveProduct = (index: number) => {
    //     if (products.length > 1) {
    //         const newProducts = products.filter((_, i) => i !== index);
    //         const newErrors = productErrors.filter((_, i) => i !== index);
    //         setProducts(newProducts);
    //         setProductErrors(newErrors);
    //     }
    // };

    // const handleProductChange = (index: number, field: 'name' | 'price', value: string) => {
    //     const newProducts = [...products];
    //     const newErrors = [...productErrors];

    //     if (field === 'price') {
    //         const regex = /^\d*\.?\d*$/;
    //         if (regex.test(value) || value === '') {
    //             newProducts[index][field] = value;
    //             newErrors[index] = { ...newErrors[index], [field]: value === '' };
    //         }
    //     } else {
    //         newProducts[index][field] = value;
    //         newErrors[index] = { ...newErrors[index], [field]: value.trim() === '' };
    //     }

    //     setProducts(newProducts);
    //     setProductErrors(newErrors);
    // };

    const handleChangePayeeEmail = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        setPayeeEmail(value);

        if (value === "") {
            setEmailError("");
        } else if (!isValidEmail(value)) {
            setEmailError("Please enter a valid email address");
        } else {
            setEmailError("");
        }
    };

    const handleChangeSecondaryMobile = (value: string) => {
        setSecondaryMobile(value);
    };

    const handleChangeSecondaryPayeeName = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSecondaryPayeeName(event.target.value);
    };

    const handleChangeSecondaryEmail = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSecondaryEmail(event.target.value);
    };

    const handleChangeOutsideState = (event: React.ChangeEvent<HTMLInputElement>) => {
        setOutsideState(event.target.checked);
    };

    const handleConsumerGstChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value.toUpperCase();
        setConsumerGst(value);
    };

    // Handler for KYC field changes
    const handleKYCFieldChange = (fieldId: number, value: any) => {
        setKycResponses(prev => ({
            ...prev,
            [fieldId]: value
        }));
    };

    // Render individual KYC fields
    const renderKYCField = (field: CustomerKYCFieldType) => {
        const currentValue = kycResponses[field.id] || '';
        
        switch (field.type) {
            case 'textfield':
                return (
                    <TextField
                        key={field.id}
                        value={currentValue}
                        onChange={(e) => handleKYCFieldChange(field.id, e.target.value)}
                        size="small"
                        label={field.fieldDescription}
                        variant="outlined"
                        fullWidth
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                '& fieldset': {
                                    borderColor: 'grey',
                                    borderRadius: '10px'
                                },
                                '&:hover fieldset': {
                                    borderColor: 'grey',
                                },
                                '&.Mui-focused fieldset': {
                                    borderColor: 'skyblue',
                                },
                                '& .MuiInputBase-input': {
                                    fontSize: '14px',
                                },
                            },
                            fontWeight: 400,
                            width: '100%',
                            maxWidth: '100%',
                            marginBottom: '16px'
                        }}
                    />
                );
            
            case 'dropdown':
                return (
                    <TextField
                        key={field.id}
                        select
                        value={currentValue}
                        onChange={(e) => handleKYCFieldChange(field.id, e.target.value)}
                        size="small"
                        label={field.fieldDescription}
                        variant="outlined"
                        fullWidth
                        SelectProps={{
                            native: true,
                        }}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                '& fieldset': {
                                    borderColor: 'grey',
                                    borderRadius: '10px'
                                },
                                '&:hover fieldset': {
                                    borderColor: 'grey',
                                },
                                '&.Mui-focused fieldset': {
                                    borderColor: 'skyblue',
                                },
                                '& .MuiInputBase-input': {
                                    fontSize: '14px',
                                },
                            },
                            fontWeight: 400,
                            width: '100%',
                            maxWidth: '100%',
                            marginBottom: '16px'
                        }}
                    >
                        
                        {field.options?.map((option, index) => (
                            <option key={index} value={option}>
                                {option}
                            </option>
                        ))}
                    </TextField>
                );
            
            case 'radio':
                return (
                    <div key={field.id} className="mb-4">
                        <FormLabel component="legend" className="text-gray-800 font-medium mb-2">
                            {field.fieldDescription}
                        </FormLabel>
                        <RadioGroup
                            value={currentValue}
                            onChange={(e) => handleKYCFieldChange(field.id, e.target.value)}
                            row
                        >
                            {field.options?.map((option, index) => (
                                <FormControlLabel
                                    key={index}
                                    value={option}
                                    control={<Radio size="small" />}
                                    label={option}
                                    sx={{
                                        '& .MuiFormControlLabel-label': {
                                            fontSize: '14px',
                        
                                        }
                                    }}
                                />
                            ))}
                        </RadioGroup>
                    </div>
                );
            
            case 'checkbox':
                return (
                    <FormControlLabel
                        key={field.id}
                        control={
                            <Checkbox 
                                checked={currentValue || false} 
                                onChange={(e) => handleKYCFieldChange(field.id, e.target.checked)}
                                size="small"
                            />
                        }
                        label={field.fieldDescription}
                        sx={{
                            '& .MuiFormControlLabel-label': {
                                fontSize: '14px',
                            },
                            marginBottom: '16px',
                            width: '100%',
                            maxWidth: '100%'
                        }}
                    />
                );
            
            default:
                return null;
        }
    };

    // -------------------------------------Error Handlers For Modal

    const [modalError, setModalError] = React.useState({
        subscriberName: false,
        payeeName: false,
        price: false,
        payeeNumber: false,
        CollectivePackPrice: false,
        monthlyPrice: false,
        secondaryMobile: false,
        email: false,
        secondaryPayeeName: false,
        secondaryEmail: false,
        customPrice: false,
        consumerGst: false
    });

    const isValidEmail = (email: string): boolean => {
        if (email === "") return true; // Empty email is allowed
        const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
        return emailRegex.test(email);
    };



    function CheckErrors(): boolean {
        setIsError(false)
        let localerror: boolean = false

        if (showPayeeName && payeeName.trim() === "") {
            setIsError(true)
            localerror = true
            setModalError((prev) => {
                return { ...prev, payeeName: true }
            })
        } else {
            setModalError((prev) => {
                return { ...prev, payeeName: false }
            })
        }

        if (subscriberName.trim() === "") {
            setIsError(true)
            localerror = true
            setModalError((prev) => {
                return { ...prev, subscriberName: true }
            })
        } else {
            setModalError((prev) => {
                return { ...prev, subscriberName: false }
            })
        }

        if (payNow) {
            if (price.trim() === "" || Number(price) < 6) {
                setIsError(true)
                localerror = true
                setModalError((prev) => {
                    return { ...prev, price: true }
                })
            } else {
                setModalError((prev) => {
                    return { ...prev, price: false }
                })
            }
        } else {
            setModalError((prev) => {
                return { ...prev, price: false }
            })
        }


        if (payeeNumber.length < 6) {
            localerror = true
            setIsError(true)
            setModalError((prev) => {
                return { ...prev, payeeNumber: true }
            })
        } else {
            setModalError((prev) => {
                return { ...prev, payeeNumber: false }
            })
        }

        if ( quickAddFields.type !== "CableTV" && PaymentCycle === SubscriptionType.Monthly) {
            if (monthlyPrice.trim() === "" || Number(monthlyPrice) < 6) {
                setIsError(true)
                localerror = true
                setModalError((prev) => {
                    return { ...prev, monthlyPrice: true }
                })
            } else {
                setModalError((prev) => {
                    return { ...prev, monthlyPrice: false }
                })
            }
        }
        
        if(PaymentCycle === SubscriptionType.MultiMonth || PaymentCycle === SubscriptionType.Count){
            if(customPrice.trim() === "" || Number(customPrice) < 6){
                setIsError(true)
                localerror = true
                setModalError((prev) => {
                    return { ...prev, customPrice: true }
                })
            } else {
                setModalError((prev) => {
                    return { ...prev, monthlyPrice: false }
                })
            }
        }

        // Check product errors
        products.forEach((product, index) => {
            if (products.length === 1 && quickAddFields.type !== "CableTV") {
                // If only one product exists, only check if name is empty

            } else {
                // For all products when more than one exists
                if (product.name.trim() === '' || product.price.trim() === '') {
                    setProductErrors(prev => prev.map((err, i) =>
                        i === index ? { name: product.name.trim() === '', price: product.price.trim() === '' } : err
                    ));
                    localerror = true;
                }
            }
        });

        if (showAdvanced && payeeEmail !== "" && !isValidEmail(payeeEmail)) {
            setIsError(true)
            localerror = true
            setModalError((prev) => ({
                ...prev,
                email: true
            }));
        } else {
            setModalError((prev) => ({
                ...prev,
                email: false
            }));
        }

        if (showAdvanced && secondaryMobile !== "" && secondaryMobile.length < 6) {
            setIsError(true)
            localerror = true
            setModalError((prev) => ({
                ...prev,
                secondaryMobile: true
            }));
        } else {
            setModalError((prev) => ({
                ...prev,
                secondaryMobile: false
            }));
        }

        if (showAdvanced && secondaryEmail !== "" && !isValidEmail(secondaryEmail)) {
            setIsError(true)
            localerror = true
            setModalError((prev) => ({
                ...prev,
                secondaryEmail: true
            }));
        } else {
            setModalError((prev) => ({
                ...prev,
                secondaryEmail: false
            }));
        }

        // Consumer GST validation (only if a value is provided)
        if (showAdvanced && consumerGst.trim() !== "") {
            const gstinRegex = /^[0-9]{2}[A-Za-z]{5}[0-9]{4}[A-Za-z]{1}[1-9A-Za-z]{1}Z[0-9A-Za-z]{1}$/;
            if (!gstinRegex.test(consumerGst)) {
                setIsError(true)
                localerror = true
                setConsumerGstError(true)
                setModalError((prev) => ({
                    ...prev,
                    consumerGst: true
                }));
            } else {
                setConsumerGstError(false)
                setModalError((prev) => ({
                    ...prev,
                    consumerGst: false
                }));
            }
        } else {
            setConsumerGstError(false)
            setModalError((prev) => ({
                ...prev,
                consumerGst: false
            }));
        }

        return localerror
    }


    // --------------------------On Submit Operation

    const {
        create,
        axiosPostRequest,
        remove
    } = useBackend(`/ServiceProviders/${ServiceProviderId}/LiveSubscriptions`)

    const subscriptionProviderId = useAppSelector(selectEffectiveUID) 
    const subscriptionName = useAppSelector((state: RootState) => state.ArbitrarySlice.ForQuickAddModal.subscriptionName)
    const subscriptionType = useAppSelector((state: RootState) => state.ArbitrarySlice.ForQuickAddModal.type)
    const align2monthlyBoundary = useAppSelector((state: RootState) => state.ArbitrarySlice.ForQuickAddModal.align2monthlyBoundary)

    async function onSubmit(e: any) {
        Dispatch(toggleLoading(true))
        e.preventDefault()

        try {
            const Localerrors = CheckErrors()
            if (Localerrors) {
                Dispatch(toggleLoading(false))
                return
            }

            const isUserExists = await CheckForPreExisitingCustomers()
            if (isUserExists) {
                Dispatch(toggleLoading(false))
                handleClose()
                return
            }

            const docRef = doc(db, `ServiceProviders/${subscriptionProviderId}/Subscriptions/`, subscriptionId);

            // Formatting for Doc Upload to firestore

            if (PaymentCycle === "Monthly") {
                const newLiveSubDoc = {
                    IsRecurringSubscription: true,
                    billingType: "Upfront",
                    cycle: "Monthly",
                    subscriptionType: "Monthly",
                    discountNegotiated: showAdvanced ? Number(discount) : 0,
                    name: showPayeeName ? payeeName : subscriberName,
                    phoneNumber: "+" + payeeNumber,
                    photoUrl: profilePhoto,
                    priceMultiplier: showMultiplier && showAdvanced ? multiplier : 1,
                    startDate: Timestamp.fromDate(new Date()),
                    updatedAt: Timestamp.fromDate(new Date()),
                    status: "Active",
                    studentName: subscriberName,
                    subscriptionName: subscriptionName,
                    subscriptionPlanRef: docRef,
                    serviceType: subscriptionType,
                    advance: Number(advance) || 0,
                    email: showAdvanced ? payeeEmail : "",
                    secondaryMobile: showAdvanced && secondaryMobile ? "+" + secondaryMobile : "",
                    secondaryPayeeName: showAdvanced && secondaryPayeeName ? secondaryPayeeName : "",
                    secondaryEmail: showAdvanced && secondaryEmail ? secondaryEmail : "",
                    products: products.map((product, index) => {
                        if (products.length === 1) {
                            // Handle single product case
                            if (product.name.trim() === '') {
                                return {}; // Send empty object if name is not filled
                            } else {
                                return {
                                    name: product.name,
                                    price: product.price.trim() === '' ? 1 : Number(product.price)
                                };
                            }
                        } else {
                            // Handle multiple products case
                            return {
                                name: product.name,
                                price: Number(product.price)
                            };
                        }
                    }),
                    ...(quickAddFields.type !== "CableTV" && { Price: Number(monthlyPrice) }),
                    outsideState: outsideState,
                    consumerGSTIN : consumerGst || "",
                    customerKYCResponses: kycResponses || {},
                }


                // Create Document
                const createdDocRef = await create(newLiveSubDoc)

                // If not paying now, close and return
                if (!payNow) {
                    handleClose()
                    Dispatch(toggleLoading(false))
                    toast.success("Subscriber added successfully!")
                    return
                }

                // Handle API call for payment
                const apiSuccess = await handleQuickAddAPICall(createdDocRef.id)
                if (apiSuccess === "error") {
                    await remove(createdDocRef.id)
                    toast.error("Error Adding Subscriber")
                    Dispatch(toggleLoading(false))
                    return
                }

                // Success case
                toast.success("Subscriber added successfully!")
                Dispatch(closeModal("AddLiveSubscriptionModalOpen"))
                resetState()

            } else if (PaymentCycle === "MultiMonth") {

                const updatedAssignedPlans = cyclePriceArray.map((plan, index) =>
                    index === selectedCyclePlanIndex ? { ...plan, price: Number(customPrice) } : plan
                )


                const newLiveSubDoc = {
                    IsRecurringSubscription: true,
                    billingType: "Upfront",
                    cycle: "MultiMonth",
                    subscriptionType: "MultiMonth",
                    billingCycle: selectedCyclePlan?.billingCycle,
                    discountNegotiated: showAdvanced ? Number(discount) : 0,
                    name: showPayeeName ? payeeName : subscriberName,
                    phoneNumber: "+" + payeeNumber,
                    photoUrl: profilePhoto,
                    priceMultiplier: showMultiplier && showAdvanced ? multiplier : 1,
                    startDate: Timestamp.fromDate(new Date()),
                    billingStart: Timestamp.fromDate(startDate),
                    billingEnd: payNow ? Timestamp.fromDate(new Date()) : Timestamp.fromDate(endDate),
                    status: payNow ? "InActive" : "Active",
                    studentName: subscriberName,
                    subscriptionName: subscriptionName,
                    subscriptionPlanRef: docRef,
                    serviceType: subscriptionType,
                    email: showAdvanced ? payeeEmail : "",
                    secondaryMobile: showAdvanced && secondaryMobile ? "+" + secondaryMobile : "",
                    secondaryPayeeName: showAdvanced && secondaryPayeeName ? secondaryPayeeName : "",
                    secondaryEmail: showAdvanced && secondaryEmail ? secondaryEmail : "",
                    advance: Number(advance) || 0,
                    assignedPlanIndex: selectedCyclePlanIndex,
                    assignedPlans: updatedAssignedPlans,
                    pauseDaysLeft: !payNow ? selectedCyclePlan?.pauseDays || 0 : 0,
                    outsideState: outsideState,
                    products: products.map((product, index) => {
                        if (products.length === 1) {
                            if (product.name.trim() === '') {
                                return {};
                            } else {
                                return {
                                    name: product.name,
                                    price: product.price.trim() === '' ? 1 : Number(product.price)
                                };
                            }
                        } else {
                            return {
                                name: product.name,
                                price: Number(product.price)
                            };
                        }
                    }),
                    consumerGSTIN : consumerGst || "",
                    customerKYCResponses: kycResponses || {},
                }

                // Create Document
                const createdDocRef = await create(newLiveSubDoc)

                // If not paying now, close and return
                if (!payNow) {
                    handleClose()
                    Dispatch(toggleLoading(false))
                    toast.success("Subscriber added successfully!")

                    return
                }
                // This Api call handles both multimonth and count
                const apiSuccess = await handleQuickMultiMonthAddAPICall(createdDocRef.id)
                if (apiSuccess === "error") {
                    await remove(createdDocRef.id)
                    toast.error("Error Adding Subscriber")
                    Dispatch(toggleLoading(false))
                    return
                }
                Dispatch(closeModal("AddLiveSubscriptionModalOpen"))
                toast.success("Subscriber added successfully!")
                resetState()
            } else if (PaymentCycle === "Count") {
                const updatedAssignedPlans = cyclePriceArray.map((plan, index) =>
                    index === selectedCyclePlanIndex ? { ...plan, price: Number(customPrice) } : plan
                )
                const newLiveSubDoc = {
                    IsRecurringSubscription: true,
                    billingType: "Upfront",
                    cycle: "Count",
                    subscriptionType: "Count",
                    billingCycle: "Count",
                    discountNegotiated: showAdvanced ? Number(discount) : 0,
                    name: showPayeeName ? payeeName : subscriberName,
                    phoneNumber: "+" + payeeNumber,
                    photoUrl: profilePhoto,
                    priceMultiplier: showMultiplier && showAdvanced ? multiplier : 1,
                    startDate: Timestamp.fromDate(new Date()),
                    billingStart: Timestamp.fromDate(startDate),
                    billingEnd: payNow ? Timestamp.fromDate(new Date()) : Timestamp.fromDate(endDate),
                    status: payNow ? "InActive" : "Active",
                    studentName: subscriberName,
                    subscriptionName: subscriptionName,
                    subscriptionPlanRef: docRef,
                    serviceType: subscriptionType,
                    email: showAdvanced ? payeeEmail : "",
                    secondaryMobile: showAdvanced && secondaryMobile ? "+" + secondaryMobile : "",
                    secondaryPayeeName: showAdvanced && secondaryPayeeName ? secondaryPayeeName : "",
                    secondaryEmail: showAdvanced && secondaryEmail ? secondaryEmail : "",
                    advance: Number(advance) || 0,
                    validity: selectedCyclePlan?.validity || 0,
                    remainingCount: !payNow ? selectedCyclePlan?.count || 0 : 0,
                    assignedPlanIndex: selectedCyclePlanIndex,
                    assignedPlans: updatedAssignedPlans,
                    outsideState: outsideState,
                    products: products.map((product, index) => {
                        if (products.length === 1) {
                            if (product.name.trim() === '') {
                                return {};
                            } else {
                                return {
                                    name: product.name,
                                    price: product.price.trim() === '' ? 1 : Number(product.price)
                                };
                            }
                        } else {
                            return {
                                name: product.name,
                                price: Number(product.price)
                            };
                        }
                    }),
                    consumerGSTIN : consumerGst || "",
                    customerKYCResponses: kycResponses || {},
                }

                // Create Document
                const createdDocRef = await create(newLiveSubDoc)

                // If not paying now, close and return
                if (!payNow) {
                    handleClose()
                    Dispatch(toggleLoading(false))
                    toast.success("Subscriber added successfully!")

                    return
                }
                // This Api call handles both multimonth and count
                const apiSuccess = await handleQuickMultiMonthAddAPICall(createdDocRef.id)
                if (apiSuccess === "error") {
                    await remove(createdDocRef.id)
                    toast.error("Error Adding Subscriber")
                    Dispatch(toggleLoading(false))
                    return
                }
                Dispatch(closeModal("AddLiveSubscriptionModalOpen"))
                toast.success("Subscriber added successfully!")
                resetState()
            }



        } catch (error) {
            console.error("Error in onSubmit:", error)
            toast.error("An unexpected error occurred")
        } finally {
            Dispatch(toggleLoading(false))
        }
    }



    async function CheckForPreExisitingCustomers() {
        // Check for pre-existing Doc and terminate
        const docRef = doc(db, `ServiceProviders/${subscriptionProviderId}/Subscriptions/`, subscriptionId);
        const options = {
            queries: [{ attribute: "phoneNumber", operator: '==', value: "+" + payeeNumber }, {
                attribute: "studentName", operator: '==', value: subscriberName
            },], referenceCheck: {
                attribute: "subscriptionPlanRef", reference: docRef
            }
        };
        const query = getQuery(`ServiceProviders/${subscriptionProviderId}/LiveSubscriptions/`, options);
        const res: any = await getDocs(query)

        if (res.docs.length != 0) {
            toast.error("Subscriber Already exist")
            return true
        }
        return false
    }

    async function handleQuickAddAPICall(livesubscriptionId: string) {

        const body = {
            providerId: ServiceProviderId,
            livesubscriptionId: livesubscriptionId,
            orderType: "online",
            chargeable: Number(price),
            recurring: false,
            cycleName: cycleName,
            secretCloudTaskToken: secretCloudTaskToken
        }
        try {
            const response = await axiosPostRequest(`${SECRETS_ENV.API_END_POINT}/api/quickOrder`, body)
            if (response.message !== "Request successful") {

                return "error"
            }
        } catch (error) {

            return "error"
        }


        return "success"
    }


    async function handleQuickMultiMonthAddAPICall(livesubscriptionId: string) {

        const updatedAssignedPlans = cyclePriceArray.map((plan, index) =>
            index === selectedCyclePlanIndex ? { ...plan, price: Number(customPrice) } : plan
        )

        const body = {
            providerId: ServiceProviderId,
            livesubscriptionId: livesubscriptionId,
            orderType: "online",
            price: Number(price),
            recurring: false,
            cycleName: cycleName,
            subscriptionId: subscriptionId,
            secretCloudTaskToken: secretCloudTaskToken,
            billingCycle: PaymentCycle === "Count" ? "Count" : selectedCyclePlan?.billingCycle,
            billingStartTime: Timestamp.fromDate(startDate),
            billingEndTime: Timestamp.fromDate(endDate),
            cyclePriceArray: updatedAssignedPlans,
            currentPlanIndex: selectedCyclePlanIndex,
        }

        try {
            const response = await axiosPostRequest(`${SECRETS_ENV.API_END_POINT}/api/quickOrderMultiMonth`, body)
            if (response.message !== "Request successful") {

                return "error"
            }
        } catch (error) {

            return "error"
        }


        return "success"
    }

    const handleChangeStartDate = (event: React.ChangeEvent<HTMLInputElement>) => {
        const [year, month, day] = event.target.value.split('-').map(Number);
        const newDate = new Date(year, month - 1, day);
        setStartDate(newDate);
        // End date will be updated by the useEffect
    };

    // FIXME: Flexible Montly aligning is down - Remove it when not necessary
    // const getAlignedEndDate = (startDate: Date, billingCycle: string): Date => {
    //     const date = new Date(startDate);

    //     // Special handling for FlexibleMonthly
    //     if (billingCycle === 'FlexibleMonthly') {
    //         date.setDate(date.getDate() + (multiMonthPlanToDaysTable[BillingCycle.FlexibleMonthly] || 30));
    //         return date;
    //     }

    //     let daysToAdd = 0;
    //     switch (billingCycle) {
    //         case BillingCycle.Quarterly:
    //             daysToAdd = multiMonthPlanToDaysTable[BillingCycle.Quarterly] || 92;  // ~3 months
    //             break;
    //         case BillingCycle.Semester:
    //             daysToAdd = multiMonthPlanToDaysTable[BillingCycle.Semester] || 184;  // ~6 months
    //             break;
    //         case BillingCycle.Annual:
    //             daysToAdd = multiMonthPlanToDaysTable[BillingCycle.Annual] || 366;  // ~12 months
    //             break;
    //         default:
    //             return date;
    //     }
    //     console.log(daysToAdd, "daysToAddAligned")
    //     date.setDate(date.getDate() + daysToAdd);
    //     date.setDate(0);  // Set to last day of previous month
    //     return date;
    // };

    // Update the useEffect that handles end date calculations


    useEffect(() => {

        if (selectedCyclePlan && startDate) {
            if (PaymentCycle === SubscriptionType.Count) {
                const validityDays = Number(selectedCyclePlan.validity) || 0;
                const newEndDate = new Date(startDate);
                newEndDate.setDate(startDate.getDate() + validityDays);
                setEndDate(newEndDate);
            } else if (PaymentCycle === SubscriptionType.MultiMonth) {
                const validityDays = Number(selectedCyclePlan.validity) || 0;
                const newEndDate = new Date(startDate);
                newEndDate.setDate(startDate.getDate() + validityDays);
                setEndDate(newEndDate);
            }
        }
    }, [selectedCyclePlan, startDate, PaymentCycle]);

    // Update the cycle plan selection handler
    const handleCyclePlanChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const selectedIndex = Number(e.target.value);
        setSelectedCyclePlanIndex(selectedIndex)
        const selected = cyclePriceArray[selectedIndex];
        setSelectedCyclePlan(selected);
        if (PaymentCycle === "Count") {
            setMonthlyPrice(selected?.price?.toString() || "");
        } else {
            setMonthlyPrice(selected?.price?.toString() || "");
        }
    };

    // Add this handler function near the other handlers
    const handleChangeEndDate = (event: React.ChangeEvent<HTMLInputElement>) => {
        const [year, month, day] = event.target.value.split('-').map(Number);
        const newDate = new Date(year, month - 1, day);
        setEndDate(newDate);
    };

    return (<React.Fragment>

        <Dialog
            sx={{
                padding: 0,
                '& .MuiPaper-root': {
                    borderRadius: '10px',
                    width: { xs: 'calc(100% - 32px)', sm: 'auto' },
                    maxWidth: { xs: 'calc(100% - 32px)', sm: '600px', md: '800px' },
                    minWidth: { sm: '600px', md: '800px' },
                    margin: { xs: '16px', sm: 'auto' },
                    maxHeight: { xs: 'calc(100vh - 32px)', sm: '90vh' },
                    overflow: 'hidden',
                    transition: 'opacity 225ms cubic-bezier(0.4, 0, 0.2, 1) 0ms, transform 225ms cubic-bezier(0.4, 0, 0.2, 1) 0ms !important'
                },
            }}
            maxWidth="md"
            fullWidth
            open={open}
            onClose={handleClose}
            PaperProps={{
                component: 'div',
            }}
        >
            <DialogContent sx={{ 
                paddingX: { xs: "16px", sm: "24px", md: "60px" }, 
                paddingY: { xs: "24px", sm: "40px", md: "50px" }, 
                maxHeight: { xs: 'calc(100vh - 32px)', sm: '90vh' }, 
                minHeight: { sm: '70vh' },
                overflowY: 'auto',
                overflowX: 'hidden',
                width: '100%',
                maxWidth: '100%',
                boxSizing: 'border-box'
            }} className={"bg-white"}>
                <div className={"header pb-3 sm:pb-[20px]"}>
                    <p className={"text-base sm:text-lg font-[600] "}>Add Student</p>
                    <p className={"text-xs sm:text-sm font-[400] "}>Add New Students to {subscriptionName}</p>
                </div>

                <div className={"absolute right-2 sm:right-4 top-2 sm:top-4"}><IconButton onClick={handleClose} size="small">
                    <CloseIcon fontSize="small" />
                </IconButton></div>

                <div className={"pt-0"}>
                    <div className={""}>
                        <div className={""}>
                            <Box
                                component={"div"}
                                sx={{}}
                            >
                                <div className="flex flex-cl justify-cente w-full items-center gap-5">
                                    <img className="inline-block size-24 rounded-full ring-2 ring-white"
                                        src={profilePhoto}
                                        alt="Profile" />
                                    <div>
                                        <div>
                                            <input
                                                type="file"
                                                id="upload-photo"
                                                accept="image/*"
                                                style={{ display: 'none' }}
                                                onChange={handlePhotoChange}
                                            />
                                            <label htmlFor="upload-photo"
                                                className="py-2 px-3 inline-flex items-center gap-x-2 text-sm font-medium rounded-lg border border-gray-200 bg-white text-gray-800 shadow-sm hover:bg-gray-50 disabled:opacity-50 disabled:pointer-events-none cursor-pointer">
                                                <svg className="flex-shrink-0 size-4"
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    width="20" height="20" viewBox="0 0 24 24" fill="none"
                                                    stroke="currentColor" strokeWidth="2" strokeLinecap="round"
                                                    strokeLinejoin="round">
                                                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                                                    <polyline points="17 8 12 3 7 8" />
                                                    <line x1="12" x2="12" y1="3" y2="15" />
                                                </svg>
                                                Upload photo
                                            </label>
                                        </div>
                                    </div>
                                </div>

                                <div className={"TextFields flex flex-col gap-4 mt-8"} style={{ width: '100%', maxWidth: '100%', boxSizing: 'border-box' }}>
                                    <div className={"flex items-center "}>
                                        <TextField
                                            onChange={handleChangeSubscriberName}
                                            value={subscriberName}
                                            size={"small"}
                                            required={true}
                                            id="Subscriber Name"
                                            label="Student Name"
                                            error={modalError.subscriberName}
                                            helperText={modalError.subscriberName ? "Student Name is required" : ""}
                                            variant="outlined"
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    '& fieldset': {
                                                        borderColor: 'grey',
                                                        borderRadius: '10px'// Default border color
                                                    },
                                                    '&:hover fieldset': {
                                                        borderColor: 'grey', // Border color on hover
                                                    },
                                                    '&.Mui-focused fieldset': {
                                                        borderColor: 'skyblue', // Border color when focused
                                                    },
                                                    '& .MuiInputBase-input': {
                                                        fontSize: '14px', // Setting font size
                                                    },
                                                },
                                                fontWeight: 400,
                                                width: '100%',
                                                maxWidth: '100%'
                                            }}
                                        />
                                        {showPayeeName &&
                                            <div className={"flex flex-col  -top-2 ml-5 relative"}><Checkbox
                                                checked={iPay}
                                                onChange={handleChangeIPay}
                                                size="small" />
                                                <p className={"absolute top-8 text-xs"}>Copy payee</p></div>}
                                    </div>

                                    {showPayeeName && <TextField
                                        onChange={handleChangePayeeName}
                                        value={payeeName}
                                        size={"small"}
                                        required={true}
                                        id="Payee_Name"
                                        label="Payee's Name"
                                        error={modalError.payeeName}
                                        helperText={modalError.payeeName ? "Name is required" : ""}
                                        variant="outlined"
                                        fullWidth
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                '& fieldset': {
                                                    borderColor: 'grey',
                                                    borderRadius: '10px'
                                                    // Default border color
                                                },
                                                '&:hover fieldset': {
                                                    borderColor: 'grey', // Border color on hover
                                                },
                                                '&.Mui-focused fieldset': {
                                                    borderColor: 'skyblue', // Border color when focused
                                                },
                                                '& .MuiInputBase-input': {
                                                    fontSize: '14px', // Setting font size
                                                },
                                            },
                                            fontWeight: 400,
                                            width: '100%',
                                            maxWidth: '100%'
                                        }}
                                    />}

                                    <div className="mb-2">
                                        <label className="block text-sm font-medium text-gray-700 ">Payee Mobile</label>
                                        <PhoneInput
                                            country={'in'}
                                            value={payeeNumber}
                                            onChange={handleChangePayeeNumber}
                                            inputStyle={{
                                                width: '100%',
                                                height: '40px',
                                                fontSize: '14px',
                                                borderRadius: '10px',
                                                borderColor: modalError.payeeNumber ? 'red' : 'grey'
                                            }}
                                            containerStyle={{
                                                width: '100%',
                                                maxWidth: '100%',
                                                marginBottom: `0px`
                                            }}
                                        />
                                        {modalError.payeeNumber && (
                                            <p className="text-red-500 text-xs mt-1">Payee Number is required</p>
                                        )}
                                    </div>

                                    <TextField
                                        value={advance}
                                        size="small"
                                        id="Advance"
                                        label="Advance / Registration Fee"
                                        onChange={handleChangeAdvance}
                                        variant="outlined"
                                        fullWidth
                                        InputProps={{
                                            startAdornment: <InputAdornment
                                                position="start">₹</InputAdornment>,
                                        }}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                '& fieldset': {
                                                    borderColor: 'grey',
                                                    borderRadius: '10px'
                                                },
                                                '&:hover fieldset': {
                                                    borderColor: 'grey',
                                                },
                                                '&.Mui-focused fieldset': {
                                                    borderColor: 'skyblue',
                                                },
                                                '& .MuiInputBase-input': {
                                                    fontSize: '14px',
                                                },
                                            },
                                            fontWeight: 400,
                                            width: '100%',
                                            maxWidth: '100%',
                                            marginBottom: '0px'
                                        }}
                                    />

                                    {PaymentCycle !== "MultiMonth" && PaymentCycle !== "Count" && quickAddFields.type !== "CableTV" && (
                                        <TextField
                                            value={monthlyPrice}
                                            size="small"
                                            id="MonthlyPrice"
                                            label="Monthly Price"
                                            error={modalError.monthlyPrice}
                                            helperText={modalError.monthlyPrice ? "Monthly Price must be minimum ₹6" : ""}
                                            onChange={handleChangeMonthlyPrice}
                                            variant="outlined"
                                            fullWidth
                                            InputProps={{
                                                startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                                            }}
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    '& fieldset': {
                                                        borderColor: 'grey',
                                                        borderRadius: '10px'// Default border color
                                                    },
                                                    '&:hover fieldset': {
                                                        borderColor: 'grey', // Border color on hover
                                                    },
                                                    '&.Mui-focused fieldset': {
                                                        borderColor: 'skyblue', // Border color when focused
                                                    },
                                                    '& .MuiInputBase-input': {
                                                        fontSize: '14px', // Setting font size
                                                    },
                                                },
                                                fontWeight: 400,
                                                width: '100%',
                                                maxWidth: '100%'
                                            }}
                                        />
                                    )}

                                    {/* FIXME: pRODUCT cODE IS HIDED AS OF NOW, REENEABLE IT WHEN NEEDED */}
                                    {/* {PaymentCycle !== "MultiMonth" && PaymentCycle !== "Count" && (
                                        <div>
                                            {products.map((product, index) => (
                                                <div key={index} className="flex items-center gap-2 mb-2">
                                                    <TextField
                                                        value={product.name}
                                                        size="small"
                                                        label={`Product ${index + 1} Name`}
                                                        onChange={(e) => handleProductChange(index, 'name', e.target.value)}
                                                        variant="outlined"
                                                        error={productErrors[index]?.name}
                                                        placeholder="Eg. Food"
                                                        helperText={productErrors[index]?.name ? "Required" : ""}
                                                        sx={{
                                                            '& .MuiOutlinedInput-root': {
                                                                '& fieldset': {
                                                                    borderColor: 'grey',
                                                                    borderRadius: '10px'// Default border color
                                                                },
                                                                '&:hover fieldset': {
                                                                    borderColor: 'grey', // Border color on hover
                                                                },
                                                                '&.Mui-focused fieldset': {
                                                                    borderColor: 'skyblue', // Border color when focused
                                                                },
                                                                '& .MuiInputBase-input': {
                                                                    fontSize: '14px', // Setting font size
                                                                },
                                                            },
                                                            fontWeight: 400,
                                                            width: '250px'
                                                        }}
                                                    />
                                                    <TextField
                                                        value={product.price}
                                                        size="small"
                                                        label={`Product ${index + 1} Price`}
                                                        onChange={(e) => handleProductChange(index, 'price', e.target.value)}
                                                        variant="outlined"
                                                        error={products.length > 1 && productErrors[index]?.price}
                                                        helperText={products.length > 1 && productErrors[index]?.price ? "Required" : ""}
                                                        InputProps={{
                                                            startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                                                        }}
                                                        sx={{
                                                            '& .MuiOutlinedInput-root': {
                                                                '& fieldset': {
                                                                    borderColor: 'grey',
                                                                    borderRadius: '10px'// Default border color
                                                                },
                                                                '&:hover fieldset': {
                                                                    borderColor: 'grey', // Border color on hover
                                                                },
                                                                '&.Mui-focused fieldset': {
                                                                    borderColor: 'skyblue', // Border color when focused
                                                                },
                                                                '& .MuiInputBase-input': {
                                                                    fontSize: '14px', // Setting font size
                                                                },
                                                            },
                                                            fontWeight: 400,
                                                            width: '150px'
                                                        }}
                                                    />
                                                    {index === products.length - 1 && (
                                                        <IconButton onClick={handleAddProduct} size="small">
                                                            <AddIcon />
                                                        </IconButton>
                                                    )}
                                                    {products.length > 1 && (
                                                        <IconButton onClick={() => handleRemoveProduct(index)} size="small">
                                                            <DeleteIcon />
                                                        </IconButton>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    )} */}

                                    {/* Plans dispalying for MultiMonth Subscription */}
                                    {PaymentCycle === "MultiMonth" && (
                                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                                            <div className="mb-2">
                                                <FormLabel component="legend" className="text-gray-800 font-medium mb-2">
                                                    Select Plan
                                                </FormLabel>
                                                <RadioGroup
                                                    value={cyclePriceArray.findIndex(plan => plan === selectedCyclePlan).toString()}
                                                    onChange={handleCyclePlanChange}
                                                >
                                                    <div className="grid grid-cols-4 gap-4 mb-2 pl-10 pr-4">
                                                        <div className="text-gray-500 text-sm">Plan Name</div>
                                                        <div className="text-gray-500 text-sm">Price</div>
                                                        <div className="text-gray-500 text-sm">Duration (days)</div>
                                                        <div></div>
                                                    </div>
                                                    {cyclePriceArray.map((plan, index) => (
                                                        <FormControlLabel
                                                            key={index}
                                                            value={index.toString()}
                                                            control={<Radio size="small" />}
                                                            label={
                                                                <div className="grid grid-cols-4 gap-4 w-full bg-white rounded-lg shadow-sm p-2">
                                                                    <div className="pl-2">{plan.billingCycle}</div>
                                                                    <div>₹{plan.price}</div>
                                                                    <div>{plan.validity} days</div>
                                                                </div>
                                                            }
                                                            sx={{
                                                                '& .MuiFormControlLabel-label': {
                                                                    fontSize: '14px',
                                                                    width: '100%'
                                                                },
                                                                marginBottom: '8px',
                                                                '&:hover': {
                                                                    backgroundColor: 'transparent'
                                                                }
                                                            }}
                                                        />
                                                    ))}
                                                </RadioGroup>
                                            </div>

                                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 items-center mb-3 sm:mb-4 w-full">
                                                <div>
                                                    <p className="text-xs font-medium mb-1 text-gray-600">Billing Start Date</p>
                                                    <DatePicker
                                                        value={dayjs(startDate)}
                                                        onChange={(newValue) => setStartDate(newValue ? newValue.toDate() : new Date())}
                                                        format="DD/MM/YYYY"
                                                        slotProps={{
                                                            textField: {
                                                                size: "small",
                                                                sx: {
                                                                    '& .MuiOutlinedInput-root': {
                                                                        '& fieldset': {
                                                                            borderColor: 'grey',
                                                                            borderRadius: '10px',
                                                                        },
                                                                        '&:hover fieldset': {
                                                                            borderColor: 'grey',
                                                                        },
                                                                        '&.Mui-focused fieldset': {
                                                                            borderColor: 'skyblue',
                                                                        },
                                                                        '& .MuiInputBase-input': {
                                                                            fontSize: '14px',
                                                                        },
                                                                    },
                                                                    width: '100%'
                                                                }
                                                            }
                                                        }}
                                                    />
                                                </div>
                                                <div className="flex items-center gap-4 ">

                                                    <div className="flex-1">
                                                        <p className="text-xs font-medium mb-1 text-gray-600">Billing End Date {align2monthlyBoundary && selectedCyclePlan?.billingCycle !== "FlexibleMonthly" ? "(Aligned to month boundary)" : ""}</p>
                                                        <DatePicker
                                                            value={dayjs(endDate)}
                                                            onChange={(newValue) => setEndDate(newValue ? newValue.toDate() : new Date())}
                                                            format="DD/MM/YYYY"
                                                            minDate={dayjs(startDate)}
                                                            disabled={payNow}
                                                            slotProps={{
                                                                textField: {
                                                                    size: "small",
                                                                    sx: {
                                                                        '& .MuiOutlinedInput-root': {
                                                                            '& fieldset': {
                                                                                borderColor: 'grey',
                                                                                borderRadius: '10px',
                                                                            },
                                                                            '&:hover fieldset': {
                                                                                borderColor: 'grey',
                                                                            },
                                                                            '&.Mui-focused fieldset': {
                                                                                borderColor: 'skyblue',
                                                                            },
                                                                            '& .MuiInputBase-input': {
                                                                                fontSize: '14px',
                                                                            },
                                                                        },
                                                                        width: '100%'
                                                                    }
                                                                }
                                                            }}
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        </LocalizationProvider>
                                    )}
                                    {/* Plans dispalying for Count Subscription */}

                                    {PaymentCycle === "Count" && (
                                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                                            <div className="mb-4">
                                                <p className="text-black font-semibold mb-2">
                                                    Select Plan
                                                </p>
                                                <RadioGroup
                                                    value={cyclePriceArray.findIndex(plan => plan === selectedCyclePlan).toString()}
                                                    onChange={handleCyclePlanChange}
                                                >
                                                    <div className="grid grid-cols-4 gap-4 mb-2 pl-10 pr-4">
                                                        <div className="text-gray-500 text-sm">Classes</div>
                                                        <div className="text-gray-500 text-sm">Price</div>
                                                        <div className="text-gray-500 text-sm">Validity (Days)</div>
                                                        <div></div>
                                                    </div>
                                                    {cyclePriceArray.map((plan, index) => (
                                                        <FormControlLabel
                                                            key={index}
                                                            value={index.toString()}
                                                            control={<Radio size="small" />}
                                                            label={
                                                                <div className="grid grid-cols-4 gap-4 w-full bg-white rounded-lg shadow-sm p-2">
                                                                    <div className="pl-2">{plan.count} classes</div>
                                                                    <div>₹{plan.price}</div>
                                                                    <div>{plan.validity} days</div>
                                                                </div>
                                                            }
                                                            sx={{
                                                                '& .MuiFormControlLabel-label': {
                                                                    fontSize: '14px',
                                                                    width: '100%'
                                                                },
                                                                marginBottom: '8px',
                                                                '&:hover': {
                                                                    backgroundColor: 'transparent'
                                                                }
                                                            }}
                                                        />
                                                    ))}
                                                </RadioGroup>
                                            </div>
                                            <p className="text-gray-800 font-medium text-sm">
                                                Subscription Period {payNow ? "" : ""}
                                            </p>
                                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 items-center mb-3 sm:mb-4 w-full">
                                                <div>
                                                    <p className="text-xs font-medium mb-1 text-gray-600">Billing Start Date</p>
                                                    <DatePicker
                                                        value={dayjs(startDate)}
                                                        onChange={(newValue) => setStartDate(newValue ? newValue.toDate() : new Date())}
                                                        format="DD/MM/YYYY"
                                                        slotProps={{
                                                            textField: {
                                                                size: "small",
                                                                sx: {
                                                                    '& .MuiOutlinedInput-root': {
                                                                        '& fieldset': {
                                                                            borderColor: 'grey',
                                                                            borderRadius: '10px',
                                                                        },
                                                                        '&:hover fieldset': {
                                                                            borderColor: 'grey',
                                                                        },
                                                                        '&.Mui-focused fieldset': {
                                                                            borderColor: 'skyblue',
                                                                        },
                                                                        '& .MuiInputBase-input': {
                                                                            fontSize: '14px',
                                                                        },
                                                                    },
                                                                    width: '100%'
                                                                }
                                                            }
                                                        }}
                                                    />
                                                </div>
                                                <div className="flex items-center gap-4">

                                                    <div className="flex-1">
                                                        <p className="text-xs font-medium mb-1 text-gray-600">Billing End Date </p>
                                                        <DatePicker
                                                            value={dayjs(endDate)}
                                                            onChange={(newValue) => setEndDate(newValue ? newValue.toDate() : new Date())}
                                                            format="DD/MM/YYYY"
                                                            minDate={dayjs(startDate)}
                                                            disabled={payNow}
                                                            slotProps={{
                                                                textField: {
                                                                    size: "small",
                                                                    sx: {
                                                                        '& .MuiOutlinedInput-root': {
                                                                            '& fieldset': {
                                                                                borderColor: 'grey',
                                                                                borderRadius: '10px',
                                                                            },
                                                                            '&:hover fieldset': {
                                                                                borderColor: 'grey',
                                                                            },
                                                                            '&.Mui-focused fieldset': {
                                                                                borderColor: 'skyblue',
                                                                            },
                                                                            '& .MuiInputBase-input': {
                                                                                fontSize: '14px',
                                                                            },
                                                                        },
                                                                        width: '100%'
                                                                    }
                                                                }
                                                            }}
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        </LocalizationProvider>

                                    )}

                                   { (PaymentCycle === SubscriptionType.Count|| PaymentCycle === SubscriptionType.MultiMonth) && <TextField
                                        value={customPrice}
                                        size="small"
                                        id="CustomPrice"
                                        label="Custom Price"
                                        onChange={handleChangeCustomPrice}
                                        error={modalError.customPrice}
                                        helperText={modalError.customPrice ? "Price must be minimum ₹6" : ""}
                                        variant="outlined"
                                        fullWidth
                                        InputProps={{
                                            startAdornment: <InputAdornment
                                                position="start">₹</InputAdornment>,
                                        }}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                '& fieldset': {
                                                    borderColor: 'grey',
                                                    borderRadius: '10px'
                                                },
                                                '&:hover fieldset': {
                                                    borderColor: 'grey',
                                                },
                                                '&.Mui-focused fieldset': {
                                                    borderColor: 'skyblue',
                                                },
                                                '& .MuiInputBase-input': {
                                                    fontSize: '14px',
                                                },
                                            },
                                            fontWeight: 400,
                                            width: '100%',
                                            maxWidth: '100%',
                                            marginBottom: '0px'
                                        }}
                                    />}

                                    <div className={"flex flex-col sm:flex-row gap-4 w-full max-w-full mt-2 items-center"}>
                                        <div className={""}>
                                            <FormControlLabel
                                                control={<Checkbox checked={payNow} onChange={handleChangePayNow} />}
                                                label="Charge Now?"
                                                sx={{
                                                    '& .MuiFormControlLabel-label': {
                                                        fontSize: '14px',
                                                    }
                                                }}
                                            />
                                        </div>

                                        {payNow && <TextField
                                            value={price}
                                            size="small"
                                            id="Chargeable"
                                            error={modalError.price}
                                            helperText={modalError.price ? "Price must be minimum ₹6" : ""}
                                            label="Chargeable"
                                            onChange={handleChangePrice}
                                            variant="outlined"
                                            InputProps={{
                                                startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                                            }}
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    '& fieldset': {
                                                        borderColor: 'grey',
                                                        borderRadius: '10px'
                                                    },
                                                    '&:hover fieldset': {
                                                        borderColor: 'grey',
                                                    },
                                                    '&.Mui-focused fieldset': {
                                                        borderColor: 'skyblue',
                                                    },
                                                    '& .MuiInputBase-input': {
                                                        fontSize: '14px',
                                                    },
                                                },
                                                fontWeight: 400,
                                                width: { xs: '100%', sm: '250px' },
                                                maxWidth: { xs: '100%', sm: '250px' }
                                            }}
                                        />}
                                    </div>


                                    {quickAddFields.type !== "CableTV" && <div>
                                        <FormControlLabel control={<Checkbox checked={showAdvanced}
                                            onChange={handleChangeShowAdvanced} />}
                                            label="Show advanced" sx={{
                                                '& .MuiFormControlLabel-label': {
                                                    fontSize: '14px', // Setting the font size for the label
                                                }
                                            }} />
                                    </div>}


                                    {showAdvanced && <div className={"w-full max-w-full"} style={{ width: '100%', maxWidth: '100%', boxSizing: 'border-box', overflow: 'hidden' }}>
                                        <div className={`flex gap-4 ${PaymentCycle === SubscriptionType.Monthly ? "mb-4" : ""}`}>
                                            {PaymentCycle === SubscriptionType.Monthly && <div className={"flex"}>
                                                <TextField
                                                    value={discount}
                                                    size="small"
                                                    id="Discount"
                                                    label="Plan Discount"
                                                    onChange={handleChangeDiscount}
                                                    variant="outlined"
                                                    fullWidth
                                                    InputProps={{
                                                        startAdornment: <InputAdornment
                                                            position="start">₹</InputAdornment>,
                                                    }}
                                                    sx={{
                                                        '& .MuiOutlinedInput-root': {
                                                            '& fieldset': {
                                                                borderColor: 'grey',
                                                                borderRadius: '10px'
                                                            },
                                                            '&:hover fieldset': {
                                                                borderColor: 'grey',
                                                            },
                                                            '&.Mui-focused fieldset': {
                                                                borderColor: 'skyblue',
                                                            },
                                                            '& .MuiInputBase-input': {
                                                                fontSize: '14px',
                                                            },
                                                        },
                                                        fontWeight: 400,
                                                        width: '280px'
                                                    }}
                                                />
                                                <Tooltip title="Note: Discount chosen shall be applicable from next cycle"
                                                    arrow>
                                                    <button className={"ml-2"} onClick={(e) => e.preventDefault()}>
                                                        <InfoIcon />
                                                    </button>
                                                </Tooltip>
                                            </div>}
                                            {PaymentCycle !== "MultiMonth" && PaymentCycle !== "Count" && showMultiplier && <div className={"flex"}>
                                                <ButtonGroup>
                                                    <StyledButton
                                                        onClick={() => setMultiplier((prev) => prev - 1)}
                                                        disabled={multiplier === 1}
                                                    >
                                                        <RemoveIcon fontSize="small" />
                                                    </StyledButton>
                                                    <StyledInput sx={{ width: 80 }} size="small"
                                                        onChange={handleChangeMultiplier}
                                                        value={multiplier} disabled
                                                    />
                                                    <StyledButton onClick={() => setMultiplier((prev) => prev + 1)}>
                                                        <AddIcon fontSize="small" />
                                                    </StyledButton>
                                                </ButtonGroup>
                                                <Tooltip
                                                    title="This student will be charged IGST instead of CGST+SGST. Note: This option is only applicable when GST is enabled for your business."
                                                    arrow
                                                >
                                                    <button className={"ml-2"} onClick={(e) => e.preventDefault()}>
                                                        <InfoIcon />
                                                    </button>
                                                </Tooltip>
                                            </div>}
                                        </div>

                                        <TextField
                                            value={payeeEmail}
                                            size="small"
                                            label="Payee Email (Optional)"
                                            type="email"
                                            onChange={handleChangePayeeEmail}
                                            variant="outlined"
                                            fullWidth
                                            error={modalError.email}
                                            helperText={modalError.email ? "Please enter a valid email address" : ""}
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    '& fieldset': {
                                                        borderColor: 'grey',
                                                        borderRadius: '10px'
                                                    },
                                                    '&:hover fieldset': {
                                                        borderColor: 'grey',
                                                    },
                                                    '&.Mui-focused fieldset': {
                                                        borderColor: 'skyblue',
                                                    },
                                                    '& .MuiInputBase-input': {
                                                        fontSize: '14px',
                                                    },
                                                },
                                                fontWeight: 400,
                                                marginBottom: 2,

                                            }}
                                        />
                                        <TextField
                                            value={secondaryPayeeName}
                                            size="small"
                                            label="Secondary Payee Name (Optional)"
                                            onChange={handleChangeSecondaryPayeeName}
                                            variant="outlined"
                                            fullWidth
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    '& fieldset': {
                                                        borderColor: 'grey',
                                                        borderRadius: '10px'
                                                    },
                                                    '&:hover fieldset': {
                                                        borderColor: 'grey',
                                                    },
                                                    '&.Mui-focused fieldset': {
                                                        borderColor: 'skyblue',
                                                    },
                                                    '& .MuiInputBase-input': {
                                                        fontSize: '14px',
                                                    },
                                                },
                                                fontWeight: 400,
                                                marginBottom: 2,

                                            }}
                                        />

                                        <div className="mb-4">
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Secondary Mobile (Optional)</label>
                                            <PhoneInput
                                                country={'in'}
                                                value={secondaryMobile}
                                                onChange={handleChangeSecondaryMobile}
                                                inputStyle={{
                                                    width: '100%',
                                                    height: '40px',
                                                    fontSize: '14px',
                                                    borderRadius: '10px',
                                                    borderColor: modalError.secondaryMobile ? 'red' : 'grey'
                                                }}
                                                containerStyle={{
                                                    width: '100%',
                                                    marginBottom: `0px`
                                                }}
                                            />
                                            {modalError.secondaryMobile && (
                                                <p className="text-red-500 text-xs mt-1">Mobile number must be valid</p>
                                            )}
                                        </div>

                                        <TextField
                                            value={secondaryEmail}
                                            size="small"
                                            label="Secondary Email (Optional)"
                                            type="email"
                                            onChange={handleChangeSecondaryEmail}
                                            variant="outlined"
                                            fullWidth
                                            error={modalError.secondaryEmail}
                                            helperText={modalError.secondaryEmail ? "Please enter a valid email address" : ""}
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    '& fieldset': {
                                                        borderColor: 'grey',
                                                        borderRadius: '10px'
                                                    },
                                                    '&:hover fieldset': {
                                                        borderColor: 'grey',
                                                    },
                                                    '&.Mui-focused fieldset': {
                                                        borderColor: 'skyblue',
                                                    },
                                                    '& .MuiInputBase-input': {
                                                        fontSize: '14px',
                                                    },
                                                },
                                                fontWeight: 400,
                                                marginBottom: 2
                                            }}
                                        />

                                        <TextField
                                            onChange={handleConsumerGstChange}
                                            value={consumerGst}
                                            size={"small"}
                                            id="ConsumerGST"
                                            label="Consumer GST (Optional)"
                                            fullWidth={true}
                                            error={consumerGstError}
                                            helperText={consumerGstError ? "Invalid GST format" : ""}
                                            variant="outlined"
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    '& fieldset': {
                                                        borderColor: 'grey',
                                                        borderRadius: '10px'
                                                    },
                                                    '&:hover fieldset': {
                                                        borderColor: 'grey',
                                                    },
                                                    '&.Mui-focused fieldset': {
                                                        borderColor: 'skyblue',
                                                    },
                                                    '& .MuiInputBase-input': {
                                                        fontSize: '14px',
                                                    },
                                                },
                                                fontWeight: 400,
                                                marginBottom: 2
                                            }}
                                        />

                                        <div className="flex items-center gap-0">
                                            <FormControlLabel
                                                control={<Checkbox checked={outsideState} onChange={handleChangeOutsideState} />}
                                                label="Outside State"
                                                sx={{
                                                    '& .MuiFormControlLabel-label': {
                                                        fontSize: '14px',
                                                    }
                                                }}
                                            />
                                            <Tooltip 
                                                className="bg-white"
                                                title="This student will be charged IGST instead of CGST+SGST. Note: This option is only applicable when GST is enabled for your business."
                                                arrow
                                            >
                                                <button onClick={(e) => e.preventDefault()}>
                                                    <InfoIcon  />
                                                </button>
                                            </Tooltip>
                                        </div>

                                    </div>}

                                    {/* Customer KYC Fields Section */}
                                    {customerKYCFields.length > 0 && (
                                        <div className="w-full max-w-full" style={{ width: '100%', maxWidth: '100%', boxSizing: 'border-box', overflow: 'hidden' }}>
                                            <div className="mb-4">
                                                <h3 className="text-lg font-semibold text-gray-800 mb-2">Additional Information</h3>

                                            </div>
                                            <div className="">
                                                {customerKYCFields.map((field) => renderKYCField(field))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </Box>
                        </div>
                    </div>


                </div>

                <div className='w-full max-w-full mt-2' style={{ width: '100%', maxWidth: '100%', boxSizing: 'border-box', overflow: 'hidden' }}>
                    {payNow ? (
                        <div>
                            <p>Student will be charged ₹{price} {Number(advance) > 0 ? "(including Advance / Registration Fee)" : ""} now, followed by ₹{
                                (() => {
                                    if (PaymentCycle === SubscriptionType.Monthly) {
                                        const basePrice = quickAddFields.type !== "CableTV"
                                            ? Number(monthlyPrice)
                                            : 0;
                                        const productsTotal = products.reduce((sum, product) => sum + Number(product.price), 0);
                                        let totalPrice = basePrice + productsTotal;

                                        if (showAdvanced) {
                                            totalPrice = totalPrice * (showMultiplier ? multiplier : 1) - Number(discount);
                                        }

                                        return totalPrice.toFixed(2);
                                    } else {
                                        return customPrice
                                    }
                                })()
                            } {(PaymentCycle === "MultiMonth" || PaymentCycle === "Count") ? "from next Cycle" : "monthly"}</p>
                        </div>
                    ) : (
                        <div>
                            <p>Student will be added as active and will be charged ₹{
                                (() => {
                                    if (PaymentCycle === SubscriptionType.Monthly) {
                                        const basePrice = quickAddFields.type !== "CableTV"
                                            ? Number(monthlyPrice)
                                            : 0;
                                        const productsTotal = products.reduce((sum, product) => sum + Number(product.price), 0);
                                        let totalPrice = basePrice + productsTotal;

                                        if (showAdvanced) {
                                            totalPrice = totalPrice * (showMultiplier ? multiplier : 1) - Number(discount);
                                        }

                                        return totalPrice.toFixed(2);
                                    }
                                    else {
                                        return customPrice
                                    }
                                })()
                            } {(PaymentCycle === "MultiMonth" || PaymentCycle === "Count") ? "from next Cycle" : "monthly"}</p>
                        </div>
                    )}
                </div>

                <div className={"flex gap-2 pt-4"}>
                    <button onClick={(e) => {
                        e.preventDefault();
                        handleClose()
                    }} disabled={isLoading}
                        className={`w-full bg-cc_bg-tertiary border border-solid border-gray-500 p-1.5 rounded-lg ${isLoading ? "cursor-not-allowed" : "cursor-pointer"}`}>Cancel
                    </button>
                    <button
                        onClick={onSubmit} disabled={isLoading}
                        className={`w-full bg-cc_brand text-white p-1.5 rounded-lg border-gray-500 flex justify-center items-center ${isLoading ? "cursor-not-allowed" : "cursor-pointer"}`}>{isLoading &&
                            <CircularProgress sx={{ color: "white" }} size={20} />}
                        <p className={"ml-2"}>{isLoading ? "Loading" : "Add Student"}</p>
                    </button>

                </div>
            </DialogContent>

        </Dialog>

    </React.Fragment>);
}